import { useAuthenticatedQuery } from './useAuthenticatedQuery';
import { fetchOverview } from '@/lib/api';

export interface CanonicalTSBData {
  ctl: number;
  atl: number;
  tsb: number;
  /** TSB time-series for sparklines: [date, value][] */
  tsbSeries: [string, number][];
  ctlSeries: [string, number][];
  atlSeries: [string, number][];
}

const EMPTY: CanonicalTSBData = {
  ctl: 0,
  atl: 0,
  tsb: 0,
  tsbSeries: [],
  ctlSeries: [],
  atlSeries: [],
};

/**
 * Single source of truth for today's CTL / ATL / TSB (Coggan–Allen formula).
 *
 * All surfaces that display freshness/form/readiness metrics MUST use this
 * hook so that every page shows the same number.
 *
 * Backend: GET /me/overview → DailyTrainingLoad table → TSB = CTL − ATL.
 * Sign convention: positive = fresh, negative = fatigued.
 */
export function useCanonicalTSB(): {
  data: CanonicalTSBData;
  isLoading: boolean;
  error: unknown;
} {
  const { data: overview, isLoading, error } = useAuthenticatedQuery({
    queryKey: ['overview', 60],
    queryFn: () => fetchOverview(60),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  if (!overview) {
    return { data: EMPTY, isLoading, error };
  }

  const today = overview.today ?? { ctl: 0, atl: 0, tsb: 0 };
  const metrics = overview.metrics ?? {};

  return {
    data: {
      ctl: typeof today.ctl === 'number' && Number.isFinite(today.ctl) ? today.ctl : 0,
      atl: typeof today.atl === 'number' && Number.isFinite(today.atl) ? today.atl : 0,
      tsb: typeof today.tsb === 'number' && Number.isFinite(today.tsb) ? today.tsb : 0,
      tsbSeries: Array.isArray(metrics.tsb) ? metrics.tsb : [],
      ctlSeries: Array.isArray(metrics.ctl) ? metrics.ctl : [],
      atlSeries: Array.isArray(metrics.atl) ? metrics.atl : [],
    },
    isLoading,
    error,
  };
}
